import React, { useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import SlideOver from '../../shared/components/SlideOver';
import FileDropzone from '../../shared/components/FileDropzone';
import { CERT_TYPE_LABELS, CERT_TYPE_VALIDITY_MONTHS } from '../../data/mappers';

const CERT_TYPES = Object.entries(CERT_TYPE_LABELS);

const isoDay = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// Certificate slide-over form (pass 5 §5F). Default expiry offset per type
// mirrors CERT_TYPE_VALIDITY_MONTHS (annual types 12mo, EICR 60, LOLER 6) —
// the same annual/other split the key-date sync hook uses. When opened
// standalone (no fixed client), a client picker is shown instead.
export default function CertificateForm({ open, onClose, client, onSaved }) {
  const { data, add } = useData();
  const { notify } = useToast();
  const [clientId, setClientId] = useState(client?.id || '');
  const [form, setForm] = useState({ type: 'gas_safety_cp12', certificate_number: '', issued_date: isoDay(new Date()), expiry_date: '', notes: '' });
  const [autoGenerateCertNumber, setAutoGenerateCertNumber] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  // Structured CP12 data (pass 7 §7C): property + appliance rows. Only
  // sent when the type is gas_safety_cp12 — the UI shape keeps it separate
  // from the generic fields so other cert types stay simple.
  const [details, setDetails] = useState({ property_address: '', appliances: [{ make: '', model: '', location: '' }] });

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const setAppliance = (index, key, value) => setDetails((current) => ({
    ...current,
    appliances: current.appliances.map((item, i) => (i === index ? { ...item, [key]: value } : item)),
  }));

  const addAppliance = () => setDetails((current) => ({ ...current, appliances: [...current.appliances, { make: '', model: '', location: '' }] }));

  const removeAppliance = (index) => setDetails((current) => ({
    ...current,
    appliances: current.appliances.length > 1
      ? current.appliances.filter((_, i) => i !== index)
      : [{ make: '', model: '', location: '' }],
  }));

  // When the type or issued date changes, prefill expiry = issued + validity
  // (only while expiry is empty or was a previous auto-prefill).
  const prefillExpiry = (type, issued) => {
    if (!issued) return '';
    const months = CERT_TYPE_VALIDITY_MONTHS[type] || 12;
    const date = new Date(`${issued}T12:00:00`);
    date.setMonth(date.getMonth() + months);
    return isoDay(date);
  };

  const changeType = (type) => setForm((current) => ({
    ...current,
    type,
    expiry_date: prefillExpiry(type, current.issued_date) || current.expiry_date,
  }));

  const changeIssued = (issued) => setForm((current) => ({
    ...current,
    issued_date: issued,
    expiry_date: prefillExpiry(current.type, issued) || current.expiry_date,
  }));

  const save = async (event) => {
    event.preventDefault();
    const targetClient = client || data.clients.find((item) => item.id === clientId);
    if (!targetClient) {
      notify('Choose a client first', 'error');
      return;
    }
    if (!form.expiry_date) {
      notify('Expiry date is required — it drives the renewal reminder', 'error');
      return;
    }
    setBusy(true);
    try {
      const isCp12 = form.type === 'gas_safety_cp12';
      const cleanAppliances = isCp12
        ? details.appliances
          .map((item) => ({
            make: item.make.trim(),
            model: item.model.trim(),
            location: item.location.trim(),
          }))
          .filter((item) => item.make || item.model || item.location)
        : [];
      await add('certificates', {
        client_id: targetClient.id,
        type: form.type,
        certificate_number: form.certificate_number,
        issued_date: form.issued_date,
        expiry_date: form.expiry_date,
        notes: form.notes,
        file: file || undefined,
        details: isCp12
          ? {
            property_address: details.property_address.trim(),
            appliances: cleanAppliances,
          }
          : undefined,
      });
      notify('Certificate saved — renewal key date synced');
      onSaved?.();
      onClose();
    } catch (err) {
      notify(err.message || 'Failed to save certificate', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SlideOver open={open} title="Add certificate" onClose={onClose}>
      <form className="drawer-form" onSubmit={save}>
        {!client && (
          <label>Client
            <select value={clientId} onChange={(event) => setClientId(event.target.value)} required>
              <option value="">Choose a client…</option>
              {data.clients.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
        )}
        <label>Type
          <select value={form.type} onChange={(event) => changeType(event.target.value)}>
            {CERT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          <div className="form-row" style={{ alignItems: 'center' }}>
            <input 
              type="checkbox" 
              checked={autoGenerateCertNumber} 
              onChange={(e) => {
                setAutoGenerateCertNumber(e.target.checked);
                if (e.target.checked) {
                  setField('certificate_number', '');
                }
              }} 
              style={{ marginBottom: '0.5rem' }}
            />
            <span style={{ marginLeft: '0.5rem' }}>Auto-generate certificate number</span>
          </div>
          <input 
            value={form.certificate_number} 
            onChange={(event) => setField('certificate_number', event.target.value)} 
            placeholder={autoGenerateCertNumber ? "Will be auto-generated" : "e.g. CP12-2291"} 
            disabled={autoGenerateCertNumber}
            style={{ marginTop: '0.5rem' }}
          />
        </label>
        <div className="form-row">
          <label>Issued<input type="date" value={form.issued_date} onChange={(event) => changeIssued(event.target.value)} required /></label>
          <label>Expires<input type="date" value={form.expiry_date} onChange={(event) => setField('expiry_date', event.target.value)} required /></label>
        </div>
        <p className="field-hint">Expiry is prefilled from the type&apos;s typical validity — adjust if the real certificate differs. A recurring key date is created automatically.</p>
        {form.type === 'gas_safety_cp12' && (
          <div className="cp12-section">
            <h4>CP12 details</h4>
            <label>Property address<input value={details.property_address} onChange={(event) => setDetails({ ...details, property_address: event.target.value })} placeholder="Gas installation property address" /></label>
            <p className="field-hint">Appliances covered by this certificate</p>
            {details.appliances.map((appliance, index) => (
              <div className="cp12-appliance" key={index}>
                <div className="form-row">
                  <label>Make<input value={appliance.make} onChange={(event) => setAppliance(index, 'make', event.target.value)} /></label>
                  <label>Model<input value={appliance.model} onChange={(event) => setAppliance(index, 'model', event.target.value)} /></label>
                </div>
                <label>Location<input value={appliance.location} onChange={(event) => setAppliance(index, 'location', event.target.value)} placeholder="e.g. Kitchen / airing cupboard" /></label>
                <button type="button" className="secondary-button cp12-remove" onClick={() => removeAppliance(index)}>Remove appliance</button>
              </div>
            ))}
            <button type="button" className="secondary-button" onClick={addAppliance}>+ Add appliance</button>
          </div>
        )}
        <label>Notes<input value={form.notes} onChange={(event) => setField('notes', event.target.value)} placeholder="Appliance / property notes" /></label>
        <label>Certificate file (PDF or photo)
          <FileDropzone
            onFiles={(files) => setFile(files[0])}
            accept="application/pdf,image/jpeg,image/png,image/webp"
            hint="PDF or image, up to 10 MB"
          />
        </label>
        <button className="primary-button" disabled={busy}>{busy ? 'Saving…' : 'Save certificate'}</button>
      </form>
    </SlideOver>
  );
}
